import { Copy, KeyRound, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import type { ApiKeyCreateResponse, ApiKeyListResponse, ApiKeySummary } from "@/lib/types";

interface ApiKeysTabProps {
  currentUserId: string;
  isAdmin: boolean;
}

export function ApiKeysTab({ currentUserId, isAdmin }: ApiKeysTabProps) {
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<ApiKeyCreateResponse | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    try {
      const res = await api<ApiKeyListResponse>("/api-keys");
      setKeys(res.items);
      setLoadError(null);
    } catch (err) {
      setKeys([]);
      setLoadError(err instanceof Error ? err.message : "Failed to load API keys");
    }
  }

  async function handleRevoke(key: ApiKeySummary) {
    if (!confirm(`Revoke "${key.name}"? This is permanent.`)) return;
    try {
      await api(`/api-keys/${key.id}`, { method: "DELETE" });
      toast.success("API key revoked");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to revoke key");
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Programmatic access tokens scoped to this team. The token is shown once on creation —
            store it somewhere safe.
          </CardDescription>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <KeyRound /> New API key
            </Button>
          </DialogTrigger>
          <CreateKeyDialog
            onCreated={(created) => {
              setCreateOpen(false);
              setRevealed(created);
              void refresh();
            }}
          />
        </Dialog>
      </CardHeader>
      <CardContent className="space-y-3">
        {loadError ? <ErrorBanner>{loadError}</ErrorBanner> : null}
        {keys === null ? (
          <div className="space-y-2">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : keys.length === 0 ? (
          <EmptyState>No API keys yet. Create one to get started.</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead aria-label="actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((key) => {
                const canRevoke =
                  !key.revoked_at && (isAdmin || key.created_by_user_id === currentUserId);
                return (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell className="font-mono text-xs">{key.prefix}…</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(key.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : "Never"}
                    </TableCell>
                    <TableCell>
                      {key.revoked_at ? (
                        <Badge variant="err">revoked</Badge>
                      ) : key.expires_at && new Date(key.expires_at) < new Date() ? (
                        <Badge variant="warn">expired</Badge>
                      ) : (
                        <Badge variant="ok">active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canRevoke ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Revoke ${key.name}`}
                          onClick={() => handleRevoke(key)}
                        >
                          <Trash2 className="text-destructive" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <RevealedKeyDialog revealed={revealed} onClose={() => setRevealed(null)} />
    </Card>
  );
}

function CreateKeyDialog({
  onCreated,
}: {
  onCreated(created: ApiKeyCreateResponse): void;
}) {
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    const expiry = expiresInDays.trim();
    setSubmitting(true);
    try {
      const created = await api<ApiKeyCreateResponse>("/api-keys", {
        method: "POST",
        body: JSON.stringify({
          name: trimmed,
          expires_in_days: expiry ? Number(expiry) : null,
        }),
      });
      onCreated(created);
      setName("");
      setExpiresInDays("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create key");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Create API key</DialogTitle>
        <DialogDescription>
          Give this key a memorable name. The full token is shown exactly once.
        </DialogDescription>
      </DialogHeader>
      <form className="grid gap-4" onSubmit={onSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="api-key-name">Name</Label>
          <Input
            id="api-key-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. ci-bot, local laptop"
            maxLength={120}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="api-key-expiry">Expires in (days)</Label>
          <Input
            id="api-key-expiry"
            type="number"
            min={1}
            max={3650}
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(event.target.value)}
            placeholder="Optional"
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={submitting || !name.trim()}>
            {submitting ? <Loader2 className="animate-spin" /> : null}
            Create key
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function RevealedKeyDialog({
  revealed,
  onClose,
}: {
  revealed: ApiKeyCreateResponse | null;
  onClose(): void;
}) {
  return (
    <Dialog open={revealed !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API key created</DialogTitle>
          <DialogDescription>
            This is the only time the token will be shown. Copy it now and store it somewhere safe.
          </DialogDescription>
        </DialogHeader>
        {revealed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={revealed.token}
                className="font-mono text-xs"
                aria-label="Generated API key"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Copy token"
                onClick={() => {
                  void navigator.clipboard.writeText(revealed.token);
                  toast.success("Copied to clipboard");
                }}
              >
                <Copy />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Prefix: <code>{revealed.prefix}…</code>
            </p>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
